import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import atlasApi from "../../utils/atlasapi";
import TurfBBox from "@turf/bbox";
import { dissolveToMultiPolygon } from "../../utils/geometry";
import { MapTrifold, MagnifyingGlass, ClipboardText, X, Plus, Copy } from "@phosphor-icons/react";

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

const SOURCE_ID = "cad-parcels";
const LAYER_IDS = ["cad-parcels-label", "cad-parcels-line", "cad-parcels-fill"];
const MIN_QUERY_LENGTH = 3;

// Splits pasted text into cadastral numbers. Accepts comma, semicolon, tab,
// newline and space as separators.
export function parseCadnumList(text) {
	return [...new Set(
		(text || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
	)];
}

const formatHa = (ha) => (ha == null ? "–" : Number(ha).toLocaleString(undefined, { maximumFractionDigits: 2 }));

/* ---- Map layers ---- */
function addCadastralLayers(map, fc) {
	if (!map.getSource(SOURCE_ID)) {
		map.addSource(SOURCE_ID, { type: "geojson", data: fc, promoteId: "cadnum" });
	} else {
		map.getSource(SOURCE_ID).setData(fc);
	}
	if (!map.getLayer("cad-parcels-fill")) {
		map.addLayer({
			id: "cad-parcels-fill",
			type: "fill",
			source: SOURCE_ID,
			paint: {
				"fill-color": ["case", ["boolean", ["feature-state", "hover"], false], "#f79009", "#12b76a"],
				"fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.55, 0.38]
			}
		});
	}
	if (!map.getLayer("cad-parcels-line")) {
		map.addLayer({
			id: "cad-parcels-line",
			type: "line",
			source: SOURCE_ID,
			paint: {
				"line-color": ["case", ["boolean", ["feature-state", "hover"], false], "#b54708", "#027a48"],
				"line-width": ["case", ["boolean", ["feature-state", "hover"], false], 2.6, 1.6]
			}
		});
	}
	if (!map.getLayer("cad-parcels-label")) {
		map.addLayer({
			id: "cad-parcels-label",
			type: "symbol",
			source: SOURCE_ID,
			minzoom: 14,
			layout: {
				"text-field": ["get", "cadnum"],
				"text-size": 11,
				"text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
				"text-allow-overlap": false
			},
			paint: {
				"text-color": "#12374f",
				"text-halo-color": "rgba(255,255,255,0.95)",
				"text-halo-width": 1.5
			}
		});
	}
}

function removeCadastralLayers(map) {
	if (!map || !map.getStyle) return;
	try {
		LAYER_IDS.forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
		if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
	} catch (e) { /* style may already be gone */ }
}

/* ---- Search box with autocomplete ---- */
function CadastralSearch({ selectedIds, onAdd, busy }) {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const [activeIdx, setActiveIdx] = useState(-1);
	const [loading, setLoading] = useState(false);
	const seqRef = useRef(0);

	useEffect(() => {
		const q = query.replace(/\s+/g, "");
		if (q.length < MIN_QUERY_LENGTH) {
			setResults([]);
			setLoading(false);
			return;
		}
		const seq = ++seqRef.current;
		setLoading(true);
		const t = setTimeout(() => {
			atlasApi.searchCadastralBG(q)
				.then((rows) => {
					if (seq !== seqRef.current) return;
					setResults(Array.isArray(rows) ? rows : []);
					setActiveIdx(-1);
				})
				.catch(() => { if (seq === seqRef.current) setResults([]); })
				.finally(() => { if (seq === seqRef.current) setLoading(false); });
		}, 200);
		return () => clearTimeout(t);
	}, [query]);

	const pick = (cadnum) => {
		onAdd([cadnum]);
		setQuery("");
		setResults([]);
		setActiveIdx(-1);
	};

	const onKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIdx((i) => Math.min(i + 1, results.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIdx((i) => Math.max(i - 1, -1));
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (activeIdx >= 0 && results[activeIdx]) pick(results[activeIdx].cadnum);
			else if (results.length === 1) pick(results[0].cadnum);
			else if (query.trim()) pick(query.replace(/\s+/g, ""));
		} else if (e.key === "Escape") {
			setResults([]);
			setActiveIdx(-1);
		}
	};

	const q = query.replace(/\s+/g, "");

	return (
		<div className="cad-search">
			<div className="cad-input">
				<MagnifyingGlass size={16} />
				<input
					type="text"
					value={query}
					placeholder="Cadastral number, e.g. 56126.990.6"
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={onKeyDown}
					disabled={busy}
					autoFocus
				/>
				{loading && <span className="spin-ring sm" />}
			</div>
			{results.length > 0 && (
				<div className="cad-results">
					{results.map((r, i) => {
						const added = selectedIds.has(r.cadnum);
						return (
							<div
								key={r.cadnum}
								className={"cad-result " + (i === activeIdx ? "active " : "") + (added ? "added" : "")}
								onMouseEnter={() => setActiveIdx(i)}
								onMouseDown={(e) => { e.preventDefault(); if (!added) pick(r.cadnum); }}>
								<div className="top">
									<span className="nm">{r.cadnum}</span>
									<span className="ha">{added ? "Added" : `${formatHa(r.areaHa)} ha`}</span>
								</div>
								<div className="area-meta">
									{[r.settlement, r.place, r.usetype].filter(Boolean).join(" · ")}
								</div>
							</div>
						);
					})}
				</div>
			)}
			{!loading && q.length >= MIN_QUERY_LENGTH && results.length === 0 && (
				<div className="cad-noresults">No cadastral numbers start with “{q}”.</div>
			)}
		</div>
	);
}

/* ---- Paste a list of numbers ---- */
function CadastralPaste({ onAdd, busy }) {
	const [open, setOpen] = useState(false);
	const [text, setText] = useState("");
	const count = useMemo(() => parseCadnumList(text).length, [text]);

	const submit = async () => {
		const ok = await onAdd(parseCadnumList(text));
		if (ok) {
			setText("");
			setOpen(false);
		}
	};

	if (!open) {
		return (
			<button className="cad-paste-toggle" onClick={() => setOpen(true)}>
				<ClipboardText size={14} /> Paste a list of numbers…
			</button>
		);
	}
	return (
		<div className="cad-paste">
			<textarea
				rows={5}
				value={text}
				placeholder={"One number per line, or separated by commas\n56126.990.6\n56126.990.7"}
				onChange={(e) => setText(e.target.value)}
				disabled={busy}
				autoFocus
			/>
			<div className="cad-paste-actions">
				<button className="btn btn-outline btn-sm" onClick={() => { setOpen(false); setText(""); }}>Cancel</button>
				<button className="btn btn-primary btn-sm" disabled={!count || busy} onClick={submit}>
					<Plus size={14} /> Add {count || ""}
				</button>
			</div>
		</div>
	);
}

/* ---- Overlay ----
   Builds a project boundary from Bulgarian cadastral numbers. The user adds
   parcels by search or by pasting a list, sees them on the map, and hands the
   dissolved MultiPolygon to onCreate (the sandbox opens DialogProject with it).

   map        : mapboxgl.Map instance (or null while it is being created)
   mapReady   : true once the map style has loaded
   visible    : show/hide the overlay (layers are removed when hidden)
   fitPadding : padding used when fitting the map to the selected parcels
   onCreate   : called with the MultiPolygon boundary of the selected parcels
   header     : replaces the panel's own header (the sandbox's New project panel
                passes its title + method tabs here)
   onDirtyChange : called with true/false as the selection becomes (non-)empty
*/
export default function CadastralOverlay({
	map,
	mapReady,
	visible = true,
	className = "",
	fitPadding = { top: 90, bottom: 120, left: 60, right: 420 },
	onCreate,
	header,
	onDirtyChange
}) {
	const popupRef = useRef(null);
	const hoverRef = useRef(null);
	const fitPendingRef = useRef(false);
	const [selected, setSelected] = useState([]);
	const [hoverId, setHoverId] = useState(null);
	const [missing, setMissing] = useState([]);
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);

	const selectedIds = useMemo(() => new Set(selected.map((f) => f.id)), [selected]);
	const fc = useMemo(() => ({ type: "FeatureCollection", features: selected }), [selected]);

	const summary = useMemo(() => {
		if (!selected.length) return null;
		const totalHa = selected.reduce((s, f) => s + (f.properties.areaHa || 0), 0);
		const boundary = dissolveToMultiPolygon(selected);
		return { totalHa, boundary, parts: boundary.coordinates.length };
	}, [selected]);

	// Fetches geometry for the given numbers and appends the ones not already
	// selected. Returns true when the request went through.
	const addCadnums = useCallback(async (cadnums) => {
		const wanted = cadnums.filter((c) => !selectedIds.has(c));
		if (!wanted.length) return true;
		setBusy(true);
		setError(null);
		try {
			const resp = await atlasApi.getCadastralFeaturesBG(wanted);
			const features = (resp.features || []).map((f) => ({ ...f, id: f.properties.cadnum }));
			setMissing(resp.missing || []);
			if (features.length) {
				fitPendingRef.current = true;
				setSelected((prev) => {
					const have = new Set(prev.map((f) => f.id));
					return [...prev, ...features.filter((f) => !have.has(f.id))];
				});
			}
			return true;
		} catch (e) {
			setError(e.message || "Could not fetch cadastral parcels");
			return false;
		} finally {
			setBusy(false);
		}
	}, [selectedIds]);

	const removeCadnum = useCallback((cadnum) => {
		setSelected((prev) => prev.filter((f) => f.id !== cadnum));
		if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
	}, []);

	const clearAll = () => {
		setSelected([]);
		setMissing([]);
		setError(null);
		if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
	};

	const zoomTo = useCallback((features, padding) => {
		if (!map || !features.length) return;
		try {
			const [minX, minY, maxX, maxY] = TurfBBox({ type: "FeatureCollection", features });
			map.fitBounds([[minX, minY], [maxX, maxY]], { padding, duration: 700, maxZoom: 16 });
		} catch (e) { /* noop */ }
	}, [map]);

	const showPopup = useCallback((feature, lngLat) => {
		if (!map) return;
		if (popupRef.current) popupRef.current.remove();
		const p = feature.properties;

		const root = document.createElement("div");
		root.className = "area-popup";
		root.innerHTML = `
			<div class="ap-header">
				<div class="ap-sub"></div>
				<div class="ap-title"></div>
			</div>
			<div class="ap-body">
				<div class="ap-stats">
					<div class="ap-stat"><label>Area</label><div class="v" data-f="area"></div></div>
					<div class="ap-stat"><label>Locality</label><div class="v" data-f="place"></div></div>
					<div class="ap-stat"><label>Use</label><div class="v" data-f="usetype"></div></div>
				</div>
				<div class="ap-btn-row">
					<button class="btn btn-outline" data-action="dismiss">Close</button>
					<button class="btn btn-primary" data-action="remove">Remove</button>
				</div>
			</div>
		`;
		// Text from the cadastre is set with textContent, never as HTML.
		root.querySelector(".ap-sub").textContent = p.settlement || "";
		root.querySelector(".ap-title").textContent = p.cadnum;
		root.querySelector('[data-f="area"]').textContent = `${formatHa(p.areaHa)} ha`;
		root.querySelector('[data-f="place"]').textContent = p.place || "–";
		root.querySelector('[data-f="usetype"]').textContent = p.usetype || "–";

		const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false, maxWidth: "320px", offset: 12 })
			.setLngLat(lngLat)
			.setDOMContent(root)
			.addTo(map);
		popupRef.current = popup;

		root.querySelector('[data-action="dismiss"]').addEventListener("click", () => popup.remove());
		root.querySelector('[data-action="remove"]').addEventListener("click", () => removeCadnum(p.cadnum));
	}, [map, removeCadnum]);

	useEffect(() => {
		onDirtyChange && onDirtyChange(selected.length > 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected.length]);

	// Add / remove the layers
	useEffect(() => {
		if (!map || !mapReady) return;
		if (!visible) {
			if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
			removeCadastralLayers(map);
			return;
		}
		let cancelled = false;
		const ensure = () => { if (!cancelled) addCadastralLayers(map, fc); };
		if (map.isStyleLoaded()) ensure();
		else map.once("styledata", ensure);
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, mapReady, visible]);

	useEffect(() => {
		return () => {
			if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
		};
	}, []);

	// Push selection changes to the map, and zoom to all parcels after an add
	useEffect(() => {
		if (!map || !mapReady || !visible) return;
		const src = map.getSource(SOURCE_ID);
		if (src) src.setData(fc);
		if (fitPendingRef.current) {
			fitPendingRef.current = false;
			zoomTo(selected, fitPadding);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, mapReady, visible, fc]);

	// Keep the hover highlight in sync between list and map
	useEffect(() => {
		if (!map || !mapReady || !visible || !map.getSource(SOURCE_ID)) return;
		const prev = hoverRef.current;
		if (prev && prev !== hoverId && selectedIds.has(prev))
			map.setFeatureState({ source: SOURCE_ID, id: prev }, { hover: false });
		if (hoverId && selectedIds.has(hoverId))
			map.setFeatureState({ source: SOURCE_ID, id: hoverId }, { hover: true });
		hoverRef.current = hoverId;
	}, [map, mapReady, visible, hoverId, selectedIds]);

	// Map interaction
	useEffect(() => {
		if (!map || !mapReady || !visible) return;
		const onClick = (e) => {
			const f = e.features && e.features[0];
			if (!f) return;
			const orig = selected.find((x) => x.id === f.properties.cadnum);
			if (orig) showPopup(orig, e.lngLat);
		};
		const onMove = (e) => {
			const f = e.features && e.features[0];
			map.getCanvas().style.cursor = "pointer";
			setHoverId(f ? f.properties.cadnum : null);
		};
		const onLeave = () => {
			map.getCanvas().style.cursor = "";
			setHoverId(null);
		};
		map.on("click", "cad-parcels-fill", onClick);
		map.on("mousemove", "cad-parcels-fill", onMove);
		map.on("mouseleave", "cad-parcels-fill", onLeave);
		return () => {
			map.off("click", "cad-parcels-fill", onClick);
			map.off("mousemove", "cad-parcels-fill", onMove);
			map.off("mouseleave", "cad-parcels-fill", onLeave);
		};
	}, [map, mapReady, visible, selected, showPopup]);

	const copyMissing = () => {
		try { navigator.clipboard.writeText(missing.join("\n")); } catch (e) { /* noop */ }
	};

	if (!visible) return null;

	// Floats over the map, or sits at the bottom of the panel when embedded
	const selectionBar = summary && (
		<div className={"selection-bar " + (header ? "in-panel" : "")}>
			<div className="sel-summary">
				<span className="sel-count">
					{selected.length} parcel{selected.length > 1 ? "s" : ""}
				</span>
				<span className="sel-meta">
					{formatHa(summary.totalHa)} ha total · {summary.parts} separate area{summary.parts > 1 ? "s" : ""}
				</span>
			</div>
			<button className="btn btn-ghost btn-sm" onClick={clearAll}>Clear</button>
			<button className="btn btn-success" onClick={() => onCreate && onCreate(summary.boundary)}>
				<Plus size={16} /> Create project
			</button>
		</div>
	);

	return (
		<div className={"screening-main screening-overlay " + className}>
			<aside className={"screening-panel cadastral-panel " + (header ? "np-embedded" : "")}>
				{header}
				{!header && (
					<div className="sp-header">
						<div className="icn-bubble"><MapTrifold size={18} /></div>
						<div>
							<h1>Cadastral</h1>
							<div className="sub">Build a project area from cadastral parcels in Bulgaria</div>
						</div>
					</div>
				)}

				<div className="sp-filter">
					<CadastralSearch selectedIds={selectedIds} onAdd={addCadnums} busy={busy} />
					<CadastralPaste onAdd={addCadnums} busy={busy} />
					{busy && <div className="cad-status"><span className="spin-ring sm" /> Fetching parcels…</div>}
					{error && <div className="cad-alert error">{error}</div>}
					{missing.length > 0 && (
						<div className="cad-alert">
							<div className="cad-alert-head">
								<b>{missing.length} not found in the cadastre</b>
								<span>
									<button title="Copy" onClick={copyMissing}><Copy size={14} /></button>
									<button title="Dismiss" onClick={() => setMissing([])}><X size={14} /></button>
								</span>
							</div>
							<div className="cad-missing-list">{missing.join(", ")}</div>
						</div>
					)}
				</div>

				<div className="sp-results">
					<div className="left">
						<span className="n">{selected.length}</span>
						<span className="lbl">parcel{selected.length === 1 ? "" : "s"} selected</span>
					</div>
					<div className="right">
						{selected.length > 0 && (
							<button className="reset-link" onClick={() => zoomTo(selected, fitPadding)}>Zoom to all</button>
						)}
					</div>
				</div>

				<div className="sp-list">
					{selected.length === 0 && (
						<div className="sp-list-empty">
							No parcels yet.<br />
							Search for a cadastral number or paste a list.
						</div>
					)}
					{selected.map((f) => {
						const p = f.properties;
						return (
							<div
								key={f.id}
								className={"area-row cad-row " + (hoverId === f.id ? "hover" : "")}
								onMouseEnter={() => setHoverId(f.id)}
								onMouseLeave={() => setHoverId(null)}
								onClick={() => zoomTo([f], 120)}>
								<div className="top">
									<span className="nm">{p.cadnum}</span>
									<button
										className="cad-remove"
										title="Remove"
										onClick={(e) => { e.stopPropagation(); removeCadnum(f.id); }}>
										<X size={14} />
									</button>
								</div>
								<div className="area-meta">
									<span><b>{formatHa(p.areaHa)}</b> ha</span>
									{p.settlement && <span>{p.settlement}</span>}
									{p.place && <span>{p.place}</span>}
								</div>
							</div>
						);
					})}
				</div>
				{header && selectionBar}
			</aside>

			{!header && selectionBar}
		</div>
	);
}
